import type {
  CompanyIntegration,
  IntegrationSettings,
  PhoneNumberIntegrationIdsMap,
} from '@entities/integration';
import type { UserInfo, UserPhoneNumber } from '@entities/user';

import type { BaseUsecase } from '@usecases/base-usecase';
import { MissingIntegrationOrNumberNotAssign } from './user-login.usecase';

export type StoreIntegrationSettingsRequest = UserInfo;
export type StoreIntegrationSettingsResponse = {
  phoneNumberIntegrationIdsMap: PhoneNumberIntegrationIdsMap;
  defaultOutNumber: number;
};

export interface StoreIntegrationRepository {
  saveSettingsBatch(batch: IntegrationSettings[]): Promise<void>;
  savePhoneNumberIntegrationIdsMap(
    map: PhoneNumberIntegrationIdsMap,
  ): Promise<void>;
}

export interface CompanyIntegrationsClient {
  fetchCompanyIntegrations(
    phoneNumbers: UserPhoneNumber[],
  ): Promise<CompanyIntegration[]>;
}

export interface IntegrationSettingClient {
  fetchIntegrationSettings(
    integrations: CompanyIntegration[],
  ): Promise<IntegrationSettings[]>;
}

export type StoreIntegrationSettingsUsecase = BaseUsecase<
  StoreIntegrationSettingsRequest,
  StoreIntegrationSettingsResponse
>;

export class StoreIntegrationSettings
  implements StoreIntegrationSettingsUsecase
{
  constructor(
    private companyIntegrationClient: CompanyIntegrationsClient,
    private integrationSettingsClient: IntegrationSettingClient,
    private integrationRepository: StoreIntegrationRepository,
  ) {}

  async run(
    info: StoreIntegrationSettingsRequest,
  ): Promise<StoreIntegrationSettingsResponse> {
    const { numbers, default_out_number_id } = info;
    const companyIntegrations =
      await this.companyIntegrationClient.fetchCompanyIntegrations(numbers);
    const integrationSettings =
      await this.integrationSettingsClient.fetchIntegrationSettings(
        companyIntegrations,
      );
    if (integrationSettings.length < 1) {
      throw new MissingIntegrationOrNumberNotAssign(
        'No active integration found or phone number not linked to one.',
      );
    }
    const phoneNumberIntegrationIdsMap = this.makePhoneNumberIntegrationIdsMap(
      companyIntegrations,
      integrationSettings,
      numbers,
    );

    await Promise.allSettled([
      this.integrationRepository.saveSettingsBatch(integrationSettings),
      this.integrationRepository.savePhoneNumberIntegrationIdsMap(
        phoneNumberIntegrationIdsMap,
      ),
    ]);

    const phoneNumberIdList = Object.keys(phoneNumberIntegrationIdsMap);

    const defaultOutNumberIsInPhoneNumberIdList = phoneNumberIdList.includes(
      default_out_number_id?.toString(),
    );

    return {
      phoneNumberIntegrationIdsMap,
      defaultOutNumber: defaultOutNumberIsInPhoneNumberIdList
        ? default_out_number_id
        : parseInt(phoneNumberIdList[0]),
    };
  }

  private makePhoneNumberIntegrationIdsMap(
    companyIntegrations: CompanyIntegration[],
    integrationSettings: IntegrationSettings[],
    phoneNumbers: UserPhoneNumber[],
  ): PhoneNumberIntegrationIdsMap {
    const integrationIdsListFromSettings = integrationSettings.map(
      (setting) => setting.integrationId,
    );

    /**
     * Those company integrations who are in the integrationIdsListFromSettings.
     */
    const filteredCompanyIntegrationList = companyIntegrations.filter(
      (integration) => {
        return integrationIdsListFromSettings.includes(integration.id);
      },
    );

    const map: PhoneNumberIntegrationIdsMap = {};

    phoneNumbers.forEach((number) => {
      const numId = number.id;

      const integrationIds = filteredCompanyIntegrationList
        .filter((integration) => integration.numberIds.includes(numId))
        .map((integration) => integration.id);

      const phoneNumberFromMap = map[numId.toString()];

      if (phoneNumberFromMap) {
        phoneNumberFromMap.integrationIds = [
          ...new Set([...phoneNumberFromMap.integrationIds, ...integrationIds]),
        ];
      } else if (integrationIds.length > 0) {
        map[numId.toString()] = {
          phoneNumber: number,
          integrationIds: [...new Set(integrationIds)],
        };
      }
    });

    return map;
  }
}
